import { navigate } from 'gatsby';
import React, { useEffect } from 'react';
import { withErrorBoundary } from 'react-error-boundary';
import { defineMessages, useIntl } from 'react-intl';

import { ErrorBoundaryFallback } from '@components/ErrorBoundaryFallback';
import PdfExportPage from '@components/PdfExport/PdfExportPage';

import * as ROUTES from '@constants/routes';

import { useSession } from '@hooks/useSession';

import {
  MedicationPdfTemplate,
  useMedicationPdfData,
} from '@views/Medications/Pdf';

const messages = defineMessages({
  back: {
    id: 'medicationsPdf.back',
    defaultMessage: 'Back to Medications',
  },
  printOrSave: {
    id: 'medicationsPdf.printOrSave',
    defaultMessage: 'Print / Save PDF',
  },
  loading: {
    id: 'medicationsPdf.loading',
    defaultMessage: 'Loading medications…',
  },
  errorState: {
    id: 'medicationsPdf.errorState',
    defaultMessage: 'Unable to load medications. Please go back and try again.',
  },
});

function sanitizePdfFilename(name: string): string {
  return name.replace(/[/\\:*?"<>|]/g, '').trim();
}

function MedicationsPrintPage() {
  const { formatMessage } = useIntl();
  const {
    state: { activeProfile, isLoadingAuth },
  } = useSession();

  const { pdfData, loading, hasLoadError } = useMedicationPdfData();

  useEffect(() => {
    if (!isLoadingAuth && !activeProfile) {
      void navigate(ROUTES.LOGIN);
    }
  }, [activeProfile, isLoadingAuth]);

  if (isLoadingAuth || !activeProfile) {
    return null;
  }

  const documentTitle = pdfData?.patientName
    ? `${sanitizePdfFilename(pdfData.patientName)} - medications`
    : 'medications';

  return (
    <PdfExportPage
      backLabel={formatMessage(messages.back)}
      documentTitle={documentTitle}
      errorText={formatMessage(messages.errorState)}
      isLoading={loading}
      isReady={!loading && !hasLoadError && !!pdfData}
      loadingText={formatMessage(messages.loading)}
      onBack={() => void navigate(ROUTES.MEDICATIONS)}
      printLabel={formatMessage(messages.printOrSave)}
    >
      {pdfData && <MedicationPdfTemplate data={pdfData} />}
    </PdfExportPage>
  );
}

export default withErrorBoundary(MedicationsPrintPage, {
  FallbackComponent: ErrorBoundaryFallback,
});
